import React, { useState } from 'react';
import {
  createBuilding, createUnit, createTenant, getUnits
} from '../services/api';

export default function Setup({ user, onComplete, onLogout }) {
  const [step, setStep] = useState(1);
  const [building, setBuilding] = useState(null);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1 state
  const [buildingName, setBuildingName] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');

  // Step 2 state
  const [unitNumber, setUnitNumber] = useState('');
  const [unitFloor, setUnitFloor] = useState('');
  const [unitRent, setUnitRent] = useState('');
  const [unitAdded, setUnitAdded] = useState(false);

  // Step 3 state
  const [selectedUnit, setSelectedUnit] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [tenantDeposit, setTenantDeposit] = useState('');
  const [tenantMoveIn, setTenantMoveIn] = useState('');
  const [tenantsAdded, setTenantsAdded] = useState([]);

  const progressPercent = ((step - 1) / 3) * 100;

  async function handleAddBuilding() {
    if (!buildingName.trim()) {
      setError('Enter building name');
      return;
    }
    if (!buildingAddress.trim()) {
      setError('Enter building address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await createBuilding({
        name: buildingName.trim(),
        address: buildingAddress.trim(),
        landlord_id: user.id
      });
      setBuilding(res.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save building');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddUnit() {
    if (!unitNumber.trim()) {
      setError('Enter unit number');
      return;
    }
    if (!unitRent || isNaN(unitRent)) {
      setError('Enter valid rent amount');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await createUnit({
        unit_number: unitNumber.trim(),
        floor: unitFloor ? parseInt(unitFloor) : null,
        rent_amount: parseFloat(unitRent),
        building_id: building.id
      });
      const res = await getUnits(building.id);
      setUnits(res.data);
      setUnitNumber('');
      setUnitFloor('');
      setUnitRent('');
      setUnitAdded(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save unit');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddTenant() {
    if (!selectedUnit) {
      setError('Select a unit');
      return;
    }
    if (!tenantName.trim()) {
      setError('Enter tenant name');
      return;
    }
    if (tenantPhone.length !== 10) {
      setError('Enter valid 10-digit phone');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await createTenant({
        name: tenantName.trim(),
        phone: tenantPhone,
        unit_id: parseInt(selectedUnit),
        deposit_amount: tenantDeposit ? parseFloat(tenantDeposit) : null,
        move_in_date: tenantMoveIn || null
      });
      setTenantsAdded([...tenantsAdded, tenantName.trim()]);
      setTenantName('');
      setTenantPhone('');
      setTenantDeposit('');
      setTenantMoveIn('');
      setSelectedUnit('');
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save tenant');
    } finally {
      setLoading(false);
    }
  }

  const stepTitles = [
    '', 'Add Your Building', 'Add Your Units', 'Add Your Tenants'
  ];
  const stepDescs = [
    '',
    'Tell us about your property',
    'Add each unit — you can add more later',
    'Add tenants for occupied units'
  ];

  return (
    <div style={{
      minHeight: '100vh', background: '#f8fafc'
    }}>
      {/* Header */}
      <div style={{
        background: '#1a56db', color: 'white',
        padding: '0 24px', height: 56,
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <span style={{ fontSize: 20, fontWeight: 700 }}>RentOS</span>
        <button
          onClick={onLogout}
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: 'white', padding: '6px 14px',
            borderRadius: 6, fontSize: 13,
            border: 'none', cursor: 'pointer'
          }}
        >
          Logout
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ background: '#e2e8f0', height: 4 }}>
        <div style={{
          background: '#1a56db',
          height: 4,
          width: progressPercent + '%',
          transition: 'width 0.4s ease'
        }} />
      </div>

      <div style={{
        maxWidth: 560, margin: '40px auto', padding: '0 24px'
      }}>
        {/* Step indicator */}
        <div style={{
          display: 'flex', alignItems: 'center',
          gap: 8, marginBottom: 8
        }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: step > n ? '#22c55e' : step === n ? '#1a56db' : '#e2e8f0',
                color: step >= n ? 'white' : '#94a3b8',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 13,
                fontWeight: 600
              }}>
                {step > n ? '✓' : n}
              </div>
              {n < 3 && (
                <div style={{
                  width: 40, height: 2,
                  background: step > n ? '#22c55e' : '#e2e8f0',
                  margin: '0 4px'
                }} />
              )}
            </div>
          ))}
          <span style={{
            color: '#64748b', fontSize: 13, marginLeft: 8
          }}>
            Step {step} of 3
          </span>
        </div>

        <h2 style={{
          fontSize: 22, fontWeight: 700,
          color: '#1e293b', marginBottom: 4
        }}>
          {stepTitles[step]}
        </h2>
        <p style={{
          color: '#64748b', fontSize: 14, marginBottom: 28
        }}>
          {stepDescs[step]}
        </p>

        <div style={{
          background: 'white', borderRadius: 12,
          border: '1px solid #e2e8f0', padding: 28
        }}>

          {/* Step 1 — Building */}
          {step === 1 && (
            <div>
              <label style={{
                display: 'block', marginBottom: 6,
                fontWeight: 500, fontSize: 14, color: '#374151'
              }}>
                Building / Property Name
              </label>
              <input
                type="text"
                placeholder="e.g. Hashvi Apartments"
                value={buildingName}
                onChange={e => setBuildingName(e.target.value)}
                style={{
                  width: '100%', padding: '11px 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 14, marginBottom: 16,
                  boxSizing: 'border-box'
                }}
              />
              <label style={{
                display: 'block', marginBottom: 6,
                fontWeight: 500, fontSize: 14, color: '#374151'
              }}>
                Full Address
              </label>
              <textarea
                placeholder="Street, Area, City, Pincode"
                value={buildingAddress}
                onChange={e => setBuildingAddress(e.target.value)}
                rows={3}
                style={{
                  width: '100%', padding: '11px 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 14, marginBottom: 20,
                  boxSizing: 'border-box', resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
              {error && (
                <p style={{
                  color: '#dc2626', fontSize: 13, marginBottom: 12
                }}>
                  {error}
                </p>
              )}
              <button
                onClick={handleAddBuilding}
                disabled={loading}
                style={{
                  width: '100%', padding: 13,
                  background: loading ? '#93c5fd' : '#1a56db',
                  color: 'white', borderRadius: 8,
                  fontSize: 15, fontWeight: 600,
                  border: 'none', cursor: 'pointer'
                }}
              >
                {loading ? 'Saving...' : 'Save Building →'}
              </button>
            </div>
          )}

          {/* Step 2 — Units */}
          {step === 2 && (
            <div>
              {units.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{
                    fontSize: 13, fontWeight: 600,
                    color: '#374151', marginBottom: 8
                  }}>
                    Units added ({units.length})
                  </p>
                  {units.map(u => (
                    <div key={u.id} style={{
                      display: 'flex', justifyContent: 'space-between',
                      padding: '8px 12px', background: '#f0fdf4',
                      borderRadius: 6, marginBottom: 6,
                      border: '1px solid #bbf7d0'
                    }}>
                      <span style={{ fontWeight: 500, fontSize: 14 }}>
                        Unit {u.unit_number}
                      </span>
                      <span style={{ color: '#16a34a', fontSize: 14 }}>
                        ₹{u.rent_amount.toLocaleString()}/month
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <label style={{
                display: 'block', marginBottom: 6,
                fontWeight: 500, fontSize: 14, color: '#374151'
              }}>
                Unit Number
              </label>
              <input
                type="text"
                placeholder="e.g. 101, Room 1, Ground Floor"
                value={unitNumber}
                onChange={e => setUnitNumber(e.target.value)}
                style={{
                  width: '100%', padding: '11px 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 14, marginBottom: 16,
                  boxSizing: 'border-box'
                }}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{
                    display: 'block', marginBottom: 6,
                    fontWeight: 500, fontSize: 14, color: '#374151'
                  }}>
                    Floor (optional)
                  </label>
                  <input
                    type="number"
                    placeholder="1, 2, 3..."
                    value={unitFloor}
                    onChange={e => setUnitFloor(e.target.value)}
                    style={{
                      width: '100%', padding: '11px 14px',
                      border: '1.5px solid #e2e8f0', borderRadius: 8,
                      fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block', marginBottom: 6,
                    fontWeight: 500, fontSize: 14, color: '#374151'
                  }}>
                    Monthly Rent (₹)
                  </label>
                  <input
                    type="number"
                    placeholder="7000"
                    value={unitRent}
                    onChange={e => setUnitRent(e.target.value)}
                    style={{
                      width: '100%', padding: '11px 14px',
                      border: '1.5px solid #e2e8f0', borderRadius: 8,
                      fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {error && (
                <p style={{
                  color: '#dc2626', fontSize: 13, marginTop: 12
                }}>
                  {error}
                </p>
              )}

              <button
                onClick={handleAddUnit}
                disabled={loading}
                style={{
                  width: '100%', padding: 13,
                  background: loading ? '#93c5fd' : '#1a56db',
                  color: 'white', borderRadius: 8,
                  fontSize: 15, fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  marginTop: 16, marginBottom: 12
                }}
              >
                {loading ? 'Adding...' : '+ Add Unit'}
              </button>

              {unitAdded && (
                <button
                  onClick={() => setStep(3)}
                  style={{
                    width: '100%', padding: 13,
                    background: '#f0fdf4',
                    color: '#166534', borderRadius: 8,
                    fontSize: 15, fontWeight: 600,
                    border: '1.5px solid #22c55e',
                    cursor: 'pointer'
                  }}
                >
                  Done Adding Units →
                </button>
              )}
            </div>
          )}

          {/* Step 3 — Tenants */}
          {step === 3 && (
            <div>
              {tenantsAdded.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{
                    fontSize: 13, fontWeight: 600,
                    color: '#374151', marginBottom: 8
                  }}>
                    Tenants added ({tenantsAdded.length})
                  </p>
                  {tenantsAdded.map((name, i) => (
                    <div key={i} style={{
                      padding: '8px 12px', background: '#f0fdf4',
                      borderRadius: 6, marginBottom: 6,
                      border: '1px solid #bbf7d0',
                      fontSize: 14, fontWeight: 500, color: '#166534'
                    }}>
                      ✓ {name}
                    </div>
                  ))}
                </div>
              )}

              <label style={{
                display: 'block', marginBottom: 6,
                fontWeight: 500, fontSize: 14, color: '#374151'
              }}>
                Select Unit
              </label>
              <select
                value={selectedUnit}
                onChange={e => setSelectedUnit(e.target.value)}
                style={{
                  width: '100%', padding: '11px 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 14, marginBottom: 16,
                  boxSizing: 'border-box', background: 'white'
                }}
              >
                <option value="">Select a unit</option>
                {units.map(u => (
                  <option key={u.id} value={u.id}>
                    {'Unit ' + u.unit_number + ' — ₹' + u.rent_amount.toLocaleString()}
                  </option>
                ))}
              </select>

              <label style={{
                display: 'block', marginBottom: 6,
                fontWeight: 500, fontSize: 14, color: '#374151'
              }}>
                Tenant Full Name
              </label>
              <input
                type="text"
                placeholder="As it should appear on receipts"
                value={tenantName}
                onChange={e => setTenantName(e.target.value)}
                style={{
                  width: '100%', padding: '11px 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 14, marginBottom: 16,
                  boxSizing: 'border-box'
                }}
              />

              <label style={{
                display: 'block', marginBottom: 6,
                fontWeight: 500, fontSize: 14, color: '#374151'
              }}>
                WhatsApp Number
              </label>
              <input
                type="tel"
                placeholder="10-digit number"
                value={tenantPhone}
                onChange={e => setTenantPhone(
                  e.target.value.replace(/\D/g, '').slice(0, 10)
                )}
                style={{
                  width: '100%', padding: '11px 14px',
                  border: '1.5px solid #e2e8f0', borderRadius: 8,
                  fontSize: 14, marginBottom: 16,
                  boxSizing: 'border-box'
                }}
              />

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr', gap: 12
              }}>
                <div>
                  <label style={{
                    display: 'block', marginBottom: 6,
                    fontWeight: 500, fontSize: 14, color: '#374151'
                  }}>
                    Security Deposit (₹)
                  </label>
                  <input
                    type="number"
                    placeholder="21000"
                    value={tenantDeposit}
                    onChange={e => setTenantDeposit(e.target.value)}
                    style={{
                      width: '100%', padding: '11px 14px',
                      border: '1.5px solid #e2e8f0', borderRadius: 8,
                      fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block', marginBottom: 6,
                    fontWeight: 500, fontSize: 14, color: '#374151'
                  }}>
                    Move-in Date
                  </label>
                  <input
                    type="date"
                    value={tenantMoveIn}
                    onChange={e => setTenantMoveIn(e.target.value)}
                    style={{
                      width: '100%', padding: '11px 14px',
                      border: '1.5px solid #e2e8f0', borderRadius: 8,
                      fontSize: 14, boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {error && (
                <p style={{
                  color: '#dc2626', fontSize: 13, marginTop: 12
                }}>
                  {error}
                </p>
              )}

              <button
                onClick={handleAddTenant}
                disabled={loading}
                style={{
                  width: '100%', padding: 13,
                  background: loading ? '#93c5fd' : '#1a56db',
                  color: 'white', borderRadius: 8,
                  fontSize: 15, fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  marginTop: 16, marginBottom: 12
                }}
              >
                {loading ? 'Adding...' : '+ Add Tenant'}
              </button>

              <button
                onClick={onComplete}
                style={{
                  width: '100%', padding: 13,
                  background: tenantsAdded.length > 0 ? '#1a56db' : '#f1f5f9',
                  color: tenantsAdded.length > 0 ? 'white' : '#94a3b8',
                  borderRadius: 8, fontSize: 15, fontWeight: 600,
                  border: 'none', cursor: 'pointer'
                }}
              >
                {tenantsAdded.length > 0
                  ? 'Go to Dashboard →'
                  : 'Skip for now →'
                }
              </button>
            </div>
          )}
        </div>

        <p style={{
          textAlign: 'center', color: '#94a3b8',
          fontSize: 12, marginTop: 16
        }}>
          You can add more buildings, units and tenants anytime from your dashboard
        </p>
      </div>
    </div>
  );
}